import React from 'react';
import { Route, Switch } from 'react-router-dom';
import styled from 'styled-components';
import Header from '@this/shared/header/header';
import Footer from '@this/shared/footer/footer';
import type UserJson from '@this/domain/user/user_json';
import User from '@this/domain/user/user';
import { Fetcher, StrictFetcher } from '@this/src/util';
import { reportError } from '@this/lib/bugsnag';
import { Select } from '@this/shared/ui/inputs/select';
import { Textarea } from '@this/shared/ui/inputs/textarea';
import { Link } from '@this/shared/ui/navigations/link';

interface Props {
  serviceId?: number;
}

type UserLoadState = 'loading' | 'loaded' | 'failed';

interface State {
  user: User | null;
  userLoadState: UserLoadState;
}

// キー・ラベル・順序は BE の MiceInquiry::TRAVEL_STYLES と一致させる
const TRAVEL_STYLES: { key: string; label: string }[] = [
  { key: 'hotel_with_group_transport', label: '宿泊＋団体交通（貸切バス等）' },
  { key: 'hotel_only', label: '宿泊のみ（交通は各自手配）' },
  { key: 'company_trip', label: '社員旅行' },
  { key: 'training_camp', label: '研修・合宿' },
  { key: 'other', label: 'その他（備考にご記入ください）' }
];

const SMALL_GROUP_MAX = 8;

function getStyles() {
  return {
    formBlock: {
      width: '100%'
    } as React.CSSProperties,
    text: {
      marginBottom: '20px'
    } as React.CSSProperties,
    subtitle: {
      fontWeight: 'bold',
      fontSize: '22px',
      marginBottom: '20px'
    } as React.CSSProperties,
    sectionNote: {
      fontSize: '14px',
      color: '#666'
    } as React.CSSProperties,
    sectionHeader: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      fontWeight: 'bold',
      fontSize: '16px',
      margin: '24px 0 8px'
    } as React.CSSProperties,
    sectionNumber: {
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '20px',
      height: '20px',
      borderRadius: '50%',
      background: '#0080cb',
      color: '#fff',
      fontSize: '12px'
    } as React.CSSProperties,
    form: {
      width: '100%'
    } as React.CSSProperties,
    formBody: {
      background: '#fff'
    } as React.CSSProperties,
    field: {
      background: '#fff',
      borderBottom: '1px solid #a0a0a0',
      padding: '10px'
    } as React.CSSProperties,
    inputArea: {} as React.CSSProperties,
    inputRow: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px'
    } as React.CSSProperties,
    unit: {
      fontSize: '14px',
      whiteSpace: 'nowrap'
    } as React.CSSProperties,
    label: {
      color: '#0080cb',
      fontWeight: 'bold',
      fontSize: '12px',
      display: 'block',
      marginBottom: '4px'
    } as React.CSSProperties,
    required: {
      color: '#dc3545',
      fontSize: '11px',
      marginLeft: '6px'
    } as React.CSSProperties,
    optional: {
      color: '#888',
      fontSize: '11px',
      marginLeft: '6px'
    } as React.CSSProperties,
    input: {
      width: '100%',
      boxSizing: 'border-box',
      minHeight: 'auto',
      padding: '0.5em',
      border: '1px solid #a0a0a0',
      borderRadius: '3px',
      boxShadow: 'inset 0 1px 3px rgb(0 0 0 / 6%)',
      background: '#fff'
    } as React.CSSProperties,
    readonlyValue: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: '8px',
      padding: '0.5em',
      border: '1px solid #e0e0e0',
      borderRadius: '3px',
      background: '#f5f5f5'
    } as React.CSSProperties,
    readonlyBadge: {
      fontSize: '10px',
      color: '#666',
      border: '1px solid #ccc',
      borderRadius: '2px',
      padding: '1px 4px',
      whiteSpace: 'nowrap'
    } as React.CSSProperties,
    fieldNote: {
      fontSize: '12px',
      color: '#666',
      marginTop: '4px'
    } as React.CSSProperties,
    smallGroupNotice: {
      fontSize: '12px',
      color: '#dc3545',
      marginTop: '4px'
    } as React.CSSProperties,
    smallGroupLink: {
      marginLeft: '8px'
    } as React.CSSProperties,
    error: {
      color: '#dc3545',
      margin: '20px 0 0',
      whiteSpace: 'pre-wrap'
    } as React.CSSProperties,
    actionButtonRow: {
      display: 'flex',
      justifyContent: 'center',
      marginTop: '20px'
    } as React.CSSProperties,
    submitButton: {
      display: 'block',
      background: 'white',
      border: '2px solid #0080cb',
      color: '#0080cb',
      fontSize: '20px',
      padding: '18px',
      borderRadius: '30px',
      margin: '20px auto 0',
      width: '200px',
      minHeight: 'unset',
      boxShadow: 'none',
      cursor: 'pointer'
    } as React.CSSProperties,
    submitButtonDisabled: {
      opacity: 0.6,
      cursor: 'not-allowed'
    } as React.CSSProperties,
    submitNote: {
      textAlign: 'center',
      fontSize: '12px',
      color: '#666',
      marginTop: '8px'
    } as React.CSSProperties,
    finishWrap: {
      marginBottom: '20px'
    } as React.CSSProperties,
    finish: {
      padding: '0'
    } as React.CSSProperties,
    finishLabel: {
      fontWeight: 'bold',
      fontSize: '22px',
      marginBottom: '20px'
    } as React.CSSProperties,
    finishText: {
      lineHeight: 1.7,
      whiteSpace: 'pre-wrap'
    } as React.CSSProperties,
    title: {
      margin: '0 0 16px',
      fontSize: '24px'
    } as React.CSSProperties,
    lead: {
      margin: '0 0 12px'
    } as React.CSSProperties
  } as const;
}

class MiceInquiries extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      user: null,
      userLoadState: 'loading'
    };
  }

  componentDidMount() {
    StrictFetcher.get<UserJson>('/users.json', 'users/index.json').then(
      response => {
        const user = new User(response);
        this.setState({ user, userLoadState: 'loaded' });
      },
      () => {
        this.setState({ user: null, userLoadState: 'failed' });
      }
    );
  }

  render() {
    try {
      const { serviceId } = this.props;
      const { user, userLoadState } = this.state;

      return (
        <div className="mice-inquiries">
          <PcOnly>
            <div className="mice-inquiries-pc">
              <Header user={user} />
              <MiceInquiriesContentWrapper>
                <Switch>
                  <Route
                    exact
                    path="/mice_inquiries/new"
                    render={() => <MiceInquiryForm user={user} userLoadState={userLoadState} />}
                  />
                </Switch>
              </MiceInquiriesContentWrapper>
              <Footer signedIn={!!user} serviceId={serviceId} />
            </div>
          </PcOnly>
        </div>
      );
    } catch (e) {
      reportError(e);
      return null;
    }
  }
}

type MiceInquiryFormProps = {
  user: User | null;
  userLoadState: UserLoadState;
};

const PcOnly = styled.div`
  min-height: 100vh;

  > :first-child {
    display: flex;
    flex-direction: column;
    min-height: 100vh;
    margin: 0 auto;
  }

  header {
    background: #292929;
    border-bottom: 1px solid #af985e;
  }

  .header-in {
    max-width: 100%;
    background: none;
    border-bottom: none;
    padding: 0 20px;
  }

  .logo {
    padding-left: 0;
  }

  .trips-button {
    margin-right: 0;
  }

  .in-advance-button {
    margin-right: 10px;
  }

  .content-wrapper {
    flex-grow: 9999;
    display: flex;
    flex-direction: column;
    min-height: 0%;
    width: 100%;
    background: #eee;
  }

  .content-body {
    max-width: 1030px;
    padding: 20px;
    background: none;
  }

  .info-content {
    position: relative;
    background: white;

    &__title {
      &__link {
        position: absolute;
        top: 10px;
        right: 10px;
        margin: 0;
      }
    }
  }
`;

const MiceInquiriesContentWrapper = styled.div`
  background-color: ${props => props.theme.grayBgColor};
  padding: 20px;
  flex-grow: 9999;
  max-width: 1000px;
  margin: 0 auto;
  width: 100%;
`;

type MiceInquiryTextFieldKey =
  | 'departure'
  | 'destination'
  | 'departureOn'
  | 'nights'
  | 'participantsCount'
  | 'budgetCapPerPerson'
  | 'budgetCapTotal'
  | 'remarks';

type MiceInquiryFormState = {
  departure: string;
  destination: string;
  departureOn: string;
  nights: string;
  participantsCount: string;
  travelStyle: string;
  budgetCapPerPerson: string;
  budgetCapTotal: string;
  remarks: string;
  loading: boolean;
  errors: string[];
  succeeded: boolean;
};

const responseErrors = (error: unknown): string[] | null => {
  if (!error || typeof error !== 'object' || !('responseJSON' in error)) return null;
  const { responseJSON } = error as { responseJSON?: { errors?: unknown } };
  const errors = responseJSON?.errors;
  return Array.isArray(errors) && errors.every(e => typeof e === 'string') ? errors : null;
};

type SectionHeaderProps = { number: number; title: string; optional?: boolean };

const SectionHeader = ({ number, title, optional = false }: SectionHeaderProps) => {
  const styles = getStyles();
  return (
    <div style={styles.sectionHeader}>
      <span style={styles.sectionNumber}>{number}</span>
      {title}
      {optional && <span style={styles.optional}>任意</span>}
    </div>
  );
};

type ReadonlyFieldProps = { label: string; value: string };

const ReadonlyField = ({ label, value }: ReadonlyFieldProps) => {
  const styles = getStyles();
  return (
    <div style={styles.field}>
      <div style={styles.label}>{label}</div>
      <div style={styles.readonlyValue}>
        <span>{value}</span>
        <span style={styles.readonlyBadge}>登録情報</span>
      </div>
    </div>
  );
};

export const MiceInquiryForm = ({ user, userLoadState }: MiceInquiryFormProps) => {
  const styles = getStyles();
  const [state, setState] = React.useState<MiceInquiryFormState>({
    departure: '',
    destination: '',
    departureOn: '',
    nights: '',
    participantsCount: '',
    travelStyle: '',
    budgetCapPerPerson: '',
    budgetCapTotal: '',
    remarks: '',
    loading: false,
    errors: [],
    succeeded: false
  });

  const handleChange = (
    key: MiceInquiryTextFieldKey,
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { value } = event.currentTarget;
    setState(prev => ({ ...prev, [key]: value }));
  };

  const applicantText = (value: string | undefined, emptyText = '') => {
    if (userLoadState === 'loading') {
      return '読み込み中';
    }
    if (userLoadState === 'failed') {
      return '取得できませんでした';
    }
    return value || emptyText;
  };

  const participantsCount = Number(state.participantsCount);
  const participantsCountValid =
    state.participantsCount !== '' && Number.isInteger(participantsCount) && participantsCount > 0;
  const showsSmallGroupNotice = participantsCountValid && participantsCount <= SMALL_GROUP_MAX;

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!state.departure || !state.destination || !state.departureOn || !state.participantsCount) {
      setState(prev => ({
        ...prev,
        errors: ['必須項目を入力してください。']
      }));
      return;
    }
    if (!participantsCountValid) {
      setState(prev => ({
        ...prev,
        errors: ['参加人数は1以上の整数で入力してください。']
      }));
      return;
    }
    setState(prev => ({ ...prev, loading: true, errors: [] }));

    try {
      await Fetcher.post('/mice_inquiries', {
        departure: state.departure,
        destination: state.destination,
        departure_on: state.departureOn,
        nights: state.nights || null,
        participants_count: participantsCount,
        travel_style: state.travelStyle || null,
        budget_cap_per_person: state.budgetCapPerPerson || null,
        budget_cap_total: state.budgetCapTotal || null,
        remarks: state.remarks || null
      });
      setState(prev => ({ ...prev, loading: false, succeeded: true, errors: [] }));
    } catch (error) {
      const errors = responseErrors(error) || [
        '通信エラーが発生しました。しばらく時間をおいて再度お試しください。'
      ];
      setState(prev => ({ ...prev, loading: false, errors }));
    }
  };

  if (state.succeeded) {
    return (
      <div style={styles.formBlock}>
        <div style={styles.finish}>
          <div style={styles.finishLabel}>送信が完了しました</div>
          <div style={styles.finishText}>
            お問い合わせありがとうございます。
            {'\n'}
            内容を確認のうえ担当者よりご連絡します。
            {'\n'}
            内容確認のため、担当者よりお電話またはメールでご連絡させていただく場合がございます。あらかじめご了承ください。
            {'\n'}
            しばらく経っても連絡がない場合は mice@aitravel.jp までご連絡ください。
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.formBlock}>
      <div style={styles.text}>
        <div style={styles.subtitle}>団体依頼フォーム</div>
        <p>9名様以上の団体でのご旅行はこちらからお送りください。内容を確認のうえ、担当者よりご連絡いたします。</p>
      </div>
      <form style={styles.form} onSubmit={handleSubmit}>
        <SectionHeader number={1} title="お申込者情報" />
        <p style={styles.sectionNote}>ご登録情報を表示しています。変更は各種設定画面より行ってください。</p>
        <div style={styles.formBody}>
          <ReadonlyField label="お名前" value={applicantText(user?.name)} />
          <ReadonlyField label="メールアドレス" value={applicantText(user?.email)} />
          <ReadonlyField label="電話番号" value={applicantText(user?.tel, '未登録')} />
        </div>

        <SectionHeader number={2} title="ご旅行内容" />
        <div style={styles.formBody}>
          <div style={styles.field}>
            <label htmlFor="departure" style={styles.label}>
              出発地
              <span style={styles.required}>必須</span>
            </label>
            <div style={styles.inputArea}>
              <input
                id="departure"
                name="departure"
                type="text"
                style={styles.input}
                value={state.departure}
                onChange={e => handleChange('departure', e)}
                required
              />
            </div>
          </div>
          <div style={styles.field}>
            <label htmlFor="destination" style={styles.label}>
              目的地
              <span style={styles.required}>必須</span>
            </label>
            <div style={styles.inputArea}>
              <input
                id="destination"
                name="destination"
                type="text"
                style={styles.input}
                value={state.destination}
                onChange={e => handleChange('destination', e)}
                required
              />
            </div>
          </div>
          <div style={styles.field}>
            <label htmlFor="departure_on" style={styles.label}>
              出発日
              <span style={styles.required}>必須</span>
            </label>
            <div style={styles.inputArea}>
              <input
                id="departure_on"
                name="departure_on"
                type="date"
                style={styles.input}
                value={state.departureOn}
                onChange={e => handleChange('departureOn', e)}
                required
              />
            </div>
          </div>
          <div style={styles.field}>
            <label htmlFor="nights" style={styles.label}>
              泊数
              <span style={styles.optional}>任意</span>
            </label>
            <div style={styles.inputRow}>
              <input
                id="nights"
                name="nights"
                type="number"
                min="0"
                style={styles.input}
                value={state.nights}
                onChange={e => handleChange('nights', e)}
              />
              <span style={styles.unit}>泊</span>
            </div>
          </div>
          <div style={styles.field}>
            <label htmlFor="participants_count" style={styles.label}>
              参加人数
              <span style={styles.required}>必須</span>
            </label>
            <div style={styles.inputRow}>
              <input
                id="participants_count"
                name="participants_count"
                type="number"
                min="1"
                step="1"
                style={styles.input}
                value={state.participantsCount}
                onChange={e => handleChange('participantsCount', e)}
                required
              />
              <span style={styles.unit}>名</span>
            </div>
            <p style={styles.fieldNote}>9名様以上のお申込みを想定しております</p>
            {showsSmallGroupNotice && (
              <p style={styles.smallGroupNotice}>
                8名以下でのご旅行は、通常の検索・予約からお申込みください
                <Link href="/dashboard" style={styles.smallGroupLink}>
                  検索・予約画面へ
                </Link>
              </p>
            )}
          </div>
          <div style={styles.field}>
            <label htmlFor="travel_style" style={styles.label}>
              旅行形態
              <span style={styles.optional}>任意</span>
            </label>
            <div style={styles.inputArea}>
              <Select
                fullWidth
                value={state.travelStyle}
                onChange={e => {
                  const { value } = e.target;
                  setState(prev => ({ ...prev, travelStyle: String(value) }));
                }}
                inputProps={{ id: 'travel_style', name: 'travel_style' }}
              >
                <option value="">選択してください</option>
                {TRAVEL_STYLES.map(travelStyle => (
                  <option key={travelStyle.key} value={travelStyle.key}>
                    {travelStyle.label}
                  </option>
                ))}
              </Select>
            </div>
          </div>
        </div>

        <SectionHeader number={3} title="ご予算" optional />
        <p style={styles.sectionNote}>どちらか一方のみの入力でも構いません</p>
        <div style={styles.formBody}>
          <div style={styles.field}>
            <label htmlFor="budget_cap_per_person" style={styles.label}>
              予算上限（一人あたり）
            </label>
            <div style={styles.inputRow}>
              <input
                id="budget_cap_per_person"
                name="budget_cap_per_person"
                type="number"
                min="0"
                style={styles.input}
                value={state.budgetCapPerPerson}
                onChange={e => handleChange('budgetCapPerPerson', e)}
              />
              <span style={styles.unit}>円</span>
            </div>
          </div>
          <div style={styles.field}>
            <label htmlFor="budget_cap_total" style={styles.label}>
              予算上限（総額）
            </label>
            <div style={styles.inputRow}>
              <input
                id="budget_cap_total"
                name="budget_cap_total"
                type="number"
                min="0"
                style={styles.input}
                value={state.budgetCapTotal}
                onChange={e => handleChange('budgetCapTotal', e)}
              />
              <span style={styles.unit}>円</span>
            </div>
          </div>
        </div>

        <SectionHeader number={4} title="ご要望・備考" optional />
        <div style={styles.formBody}>
          <div style={styles.field}>
            <Textarea
              id="remarks"
              name="remarks"
              fullWidth
              rowsMin={4}
              placeholder="禁煙希望、会場からの距離、朝食有無など、ご要望があればご記入ください"
              value={state.remarks}
              onChange={e => handleChange('remarks', e)}
            />
          </div>
        </div>
        {state.errors.length > 0 && <div style={styles.error}>{state.errors.join('\n')}</div>}
        <div style={styles.actionButtonRow}>
          <button
            type="submit"
            style={{ ...styles.submitButton, ...(state.loading ? styles.submitButtonDisabled : {}) }}
            disabled={state.loading}
          >
            {state.loading ? '送信中' : '送信'}
          </button>
        </div>
        <p style={styles.submitNote}>送信内容確認後、担当者よりお電話またはメールでご連絡する場合があります</p>
      </form>
    </div>
  );
};

export default MiceInquiries;
